import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, DollarSign, Activity, Users, TrendingUp } from 'lucide-react';
import api from '../services/api';

interface UsageOverview {
  totalCost: string;
  totalRequests: number;
  totalUsers: number;
  breakdown: {
    provider: string;
    cost: string;
    requests: number;
  }[];
  period: {
    startDate: string;
    endDate: string;
  };
}

interface TopUser {
  userId: number;
  email: string;
  fullName: string;
  totalCost: string;
  requestCount: number;
}

export default function ApiUsageAnalyticsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [overview, setOverview] = useState<UsageOverview | null>(null);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'month' | 'week' | 'day'>('month');

  // Check if user is admin
  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Calculate date range based on selected period
  const getDateRange = () => {
    const now = new Date();
    let startDate: Date;

    switch (selectedPeriod) {
      case 'day':
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
    }

    return {
      startDate: startDate.toISOString(),
      endDate: now.toISOString(),
    };
  };

  // Fetch usage data
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);
        const dateRange = getDateRange();

        // Fetch overview
        const overviewResponse = await api.get('/usage/overview', {
          params: dateRange,
        });
        setOverview(overviewResponse.data.data);

        // Fetch top users
        const topUsersResponse = await api.get('/usage/top-users', {
          params: { ...dateRange, limit: 10 },
        });
        setTopUsers(topUsersResponse.data.data.users);
      } catch (error) {
        console.error('Failed to fetch usage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsageData();
  }, [selectedPeriod]);

  const formatCurrency = (value: string) => {
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'day':
        return 'Today';
      case 'week':
        return 'Last 7 Days';
      case 'month':
      default:
        return 'This Month';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading analytics...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">API Usage Analytics</h1>
              <p className="text-gray-500 mt-1">Monitor API costs and usage patterns</p>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex gap-2">
            <Button
              variant={selectedPeriod === 'day' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('day')}
              size="sm"
            >
              Today
            </Button>
            <Button
              variant={selectedPeriod === 'week' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('week')}
              size="sm"
            >
              Week
            </Button>
            <Button
              variant={selectedPeriod === 'month' ? 'default' : 'outline'}
              onClick={() => setSelectedPeriod('month')}
              size="sm"
            >
              Month
            </Button>
          </div>
        </div>

        {/* Overview Stats */}
        {overview && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Cost
                  </CardTitle>
                  <DollarSign className="w-4 h-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatCurrency(overview.totalCost)}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatPeriodLabel()}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Total Requests
                  </CardTitle>
                  <Activity className="w-4 h-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {overview.totalRequests.toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">API calls made</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Active Users
                  </CardTitle>
                  <Users className="w-4 h-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {overview.totalUsers}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Using AI features</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Avg Cost/Request
                  </CardTitle>
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-gray-900">
                    {formatCurrency(
                      (parseFloat(overview.totalCost) / overview.totalRequests || 0).toFixed(6)
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Per API call</p>
                </CardContent>
              </Card>
            </div>

            {/* Provider Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle>API Provider Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {overview.breakdown.map((item) => (
                    <div key={item.provider} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 capitalize">
                            {item.provider}
                          </span>
                          <span className="text-sm text-gray-500">
                            ({item.requests.toLocaleString()} requests)
                          </span>
                        </div>
                        <div className="mt-2 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${
                                (parseFloat(item.cost) / parseFloat(overview.totalCost)) * 100
                              }%`,
                            }}
                          />
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="font-bold text-gray-900">
                          {formatCurrency(item.cost)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {((parseFloat(item.cost) / parseFloat(overview.totalCost)) * 100).toFixed(
                            1
                          )}
                          %
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle>Top Users by Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">User</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Email</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Requests</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Total Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map((user) => (
                    <tr key={user.userId} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{user.fullName}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{user.email}</td>
                      <td className="py-3 px-4 text-right text-gray-900">
                        {user.requestCount.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-gray-900">
                        {formatCurrency(user.totalCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
